/**
 * CustomFraudRuleEnginePerCampaignPerPartnerPage
 *
 * Custom Fraud Rule Engine — Per Campaign / Per Partner.
 *
 * Ported from the pre-nuke "Per-partner fraud slice" report
 * (legacy path: apps/dash-legacy/src/routes/_authed/fraud/reports/per-partner.tsx).
 *
 * Legacy behaviour preserved:
 *  - period selector (7d / 30d / 90d) drives the list query
 *  - per-affiliate table: affiliate_id, total conversions, flagged, fraud rate %, blocked clicks
 *  - fraud-rate color bands (emerald < 5% < yellow < 10% < amber < 20% < rose)
 *  - horizontal progress bar per row sized by 2 * fraud_rate (capped at 100%)
 *  - loading skeletons + empty-state copy
 *
 * Modernisation:
 *  - plain Tailwind (no shadcn <Select>, no lucide icons, no zod search schema)
 *  - TanStack Query via ./hooks (no legacy `usePerPartnerSlice`)
 *  - emerald -> sky gradient H1, zinc borders, rounded-md / rounded-lg
 *  - a rule-level create / delete surface (the "engine" side of the feature)
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useCustomFraudRuleEnginePerCampaignPerPartnerList,
	useCustomFraudRuleEnginePerCampaignPerPartnerCreate,
	useCustomFraudRuleEnginePerCampaignPerPartnerDelete,
} from "./hooks";
import type { PerPartnerFraudRow } from "./api";

const PERIODS: ReadonlyArray<{ value: string; label: string }> = [
	{ value: "7d", label: "Last 7 days" },
	{ value: "30d", label: "Last 30 days" },
	{ value: "90d", label: "Last 90 days" },
];

const CARD =
	"rounded-lg border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const SELECT =
	"rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const BTN =
	"rounded-md border border-sky-600 bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-md border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

function rateTextClass(rate: number): string {
	if (rate >= 20) return "text-rose-700";
	if (rate >= 10) return "text-amber-600";
	if (rate >= 5) return "text-yellow-600";
	return "text-emerald-700";
}

function rateBarClass(rate: number): string {
	if (rate >= 20) return "bg-rose-400";
	if (rate >= 10) return "bg-amber-400";
	if (rate >= 5) return "bg-yellow-400";
	return "bg-emerald-400";
}

function coalesceRows(
	items: Array<{ rows?: PerPartnerFraudRow[] }>,
	rootRows: PerPartnerFraudRow[] | undefined,
): PerPartnerFraudRow[] {
	if (rootRows && rootRows.length > 0) return rootRows;
	const out: PerPartnerFraudRow[] = [];
	for (const it of items) {
		if (Array.isArray(it.rows)) {
			for (const r of it.rows) out.push(r);
		}
	}
	return out;
}

export function CustomFraudRuleEnginePerCampaignPerPartnerPage() {
	const [period, setPeriod] = useState<string>("30d");

	const listQuery = useCustomFraudRuleEnginePerCampaignPerPartnerList(period);
	const createMut = useCustomFraudRuleEnginePerCampaignPerPartnerCreate();
	const deleteMut = useCustomFraudRuleEnginePerCampaignPerPartnerDelete();

	const [name, setName] = useState("");
	const [campaignId, setCampaignId] = useState("");
	const [partnerId, setPartnerId] = useState("");

	const items = listQuery.data?.items ?? [];
	const rows = useMemo(
		() => coalesceRows(items, listQuery.data?.rows),
		[items, listQuery.data?.rows],
	);

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		if (!name.trim()) return;
		createMut.mutate(
			{
				name: name.trim(),
				campaign_id: campaignId.trim() || undefined,
				partner_id: partnerId.trim() || undefined,
			},
			{
				onSuccess: () => {
					setName("");
					setCampaignId("");
					setPartnerId("");
				},
			},
		);
	};

	return (
		<main
			data-testid="custom-fraud-rule-engine-per-campaign-per-partner-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 font-sans text-zinc-900"
		>
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
						custom-fraud-rule-engine-per-campaign-per-partner
					</p>
					<h1 className="mt-1 bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
						Custom Fraud Rule Engine
					</h1>
					<p className="mt-1 text-sm text-zinc-500">
						Fraud rate and blocked-click counts per affiliate, scoped per
						campaign and per partner.
					</p>
				</div>
				<label className="flex items-center gap-2 text-sm text-zinc-600">
					<span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
						Period
					</span>
					<select
						className={SELECT}
						value={period}
						onChange={(e) => setPeriod(e.currentTarget.value)}
						data-testid="custom-fraud-rule-engine-per-campaign-per-partner-period"
						aria-label="Period"
					>
						{PERIODS.map((p) => (
							<option key={p.value} value={p.value}>
								{p.label}
							</option>
						))}
					</select>
				</label>
			</header>

			<section
				className={CARD}
				aria-label="Create custom-fraud-rule-engine-per-campaign-per-partner"
			>
				<h2 className="text-base font-semibold text-zinc-900">Create rule</h2>
				<p className="mt-1 text-xs text-zinc-500">
					Rules are scoped to an (optional) campaign and partner.
				</p>
				<form
					onSubmit={handleCreate}
					className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-4"
					data-testid="custom-fraud-rule-engine-per-campaign-per-partner-create-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="Rule name"
						value={name}
						onChange={(e) => setName(e.currentTarget.value)}
						disabled={createMut.isPending}
						data-testid="custom-fraud-rule-engine-per-campaign-per-partner-name-input"
					/>
					<input
						type="text"
						className={INPUT}
						placeholder="Campaign ID (optional)"
						value={campaignId}
						onChange={(e) => setCampaignId(e.currentTarget.value)}
						disabled={createMut.isPending}
						data-testid="custom-fraud-rule-engine-per-campaign-per-partner-campaign-input"
					/>
					<input
						type="text"
						className={INPUT}
						placeholder="Partner ID (optional)"
						value={partnerId}
						onChange={(e) => setPartnerId(e.currentTarget.value)}
						disabled={createMut.isPending}
						data-testid="custom-fraud-rule-engine-per-campaign-per-partner-partner-input"
					/>
					<button
						type="submit"
						className={BTN}
						disabled={createMut.isPending || !name.trim()}
						data-testid="custom-fraud-rule-engine-per-campaign-per-partner-submit"
					>
						{createMut.isPending ? "Saving." : "Create"}
					</button>
				</form>
				{createMut.error ? (
					<p className="mt-2 text-xs text-rose-700" role="alert">
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section
				className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm"
				aria-label="Per-partner fraud slice"
			>
				<div className="border-b border-zinc-100 px-5 py-3">
					<h2 className="text-base font-semibold text-zinc-900">
						Per-partner fraud slice
					</h2>
					<p className="mt-0.5 text-xs text-zinc-500">
						One row per affiliate for the selected period.
					</p>
				</div>
				{listQuery.isLoading ? (
					<div
						className="divide-y divide-zinc-100"
						data-testid="custom-fraud-rule-engine-per-campaign-per-partner-loading"
					>
						{Array.from({ length: 8 }).map((_, i) => (
							<div key={i} className="flex items-center gap-6 px-5 py-3">
								<div className="h-3 w-28 animate-pulse rounded bg-zinc-100" />
								<div className="h-3 w-16 animate-pulse rounded bg-zinc-100" />
								<div className="ml-auto h-3 w-12 animate-pulse rounded bg-zinc-100" />
							</div>
						))}
					</div>
				) : listQuery.error ? (
					<p className="px-5 py-8 text-sm text-rose-700" role="alert">
						{listQuery.error.message}
					</p>
				) : rows.length === 0 ? (
					<div
						className="flex flex-col items-center justify-center gap-2 py-16 text-center"
						data-testid="custom-fraud-rule-engine-per-campaign-per-partner-empty"
					>
						<p className="text-sm text-zinc-500">
							No conversion data in this period.
						</p>
					</div>
				) : (
					<table
						className="w-full text-sm"
						data-testid="custom-fraud-rule-engine-per-campaign-per-partner-rows"
					>
						<thead>
							<tr className="border-b border-zinc-100 bg-zinc-50 text-left text-xs font-medium uppercase tracking-wide text-zinc-500">
								<th className="px-5 py-2.5">Affiliate ID</th>
								<th className="px-5 py-2.5 text-right">Total conv.</th>
								<th className="px-5 py-2.5 text-right">Flagged</th>
								<th className="px-5 py-2.5 text-right">Fraud rate</th>
								<th className="px-5 py-2.5 text-right">Blocked clicks</th>
								<th className="w-40 px-5 py-2.5" />
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{rows.map((row) => (
								<tr
									key={row.affiliate_id}
									className="hover:bg-zinc-50"
									data-testid={`custom-fraud-rule-engine-per-campaign-per-partner-fraud-row-${row.affiliate_id}`}
								>
									<td className="px-5 py-2.5 font-mono text-xs text-zinc-700">
										{row.affiliate_id}
									</td>
									<td className="px-5 py-2.5 text-right tabular-nums text-zinc-700">
										{row.total_conversions.toLocaleString()}
									</td>
									<td className="px-5 py-2.5 text-right tabular-nums text-zinc-700">
										{row.flagged_conversions.toLocaleString()}
									</td>
									<td
										className={`px-5 py-2.5 text-right tabular-nums font-medium ${rateTextClass(row.fraud_rate)}`}
									>
										{row.fraud_rate.toFixed(1)}%
									</td>
									<td className="px-5 py-2.5 text-right tabular-nums text-zinc-700">
										{row.blocked_clicks.toLocaleString()}
									</td>
									<td className="px-5 py-2.5">
										<div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
											<div
												className={`h-full rounded-full ${rateBarClass(row.fraud_rate)}`}
												style={{
													width: `${Math.min(row.fraud_rate * 2, 100)}%`,
												}}
											/>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</section>

			<section className={CARD} aria-label="Rule list">
				<h2 className="text-base font-semibold text-zinc-900">Rules</h2>
				{listQuery.isLoading ? (
					<p className="mt-3 text-sm text-zinc-500">Loading.</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="custom-fraud-rule-engine-per-campaign-per-partner-rules-empty"
					>
						No rules yet.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="custom-fraud-rule-engine-per-campaign-per-partner-list"
					>
						{items.map((row) => (
							<li
								key={row.id}
								className="flex items-center justify-between gap-3 py-2 text-sm"
								data-testid={`custom-fraud-rule-engine-per-campaign-per-partner-row-${row.id}`}
							>
								<div className="flex min-w-0 flex-col">
									<span className="truncate font-medium text-zinc-900">
										{row.name ?? row.id}
									</span>
									<span className="truncate font-mono text-xs text-zinc-500">
										{row.id}
										{row.campaign_id ? ` / campaign:${row.campaign_id}` : ""}
										{row.partner_id ? ` / partner:${row.partner_id}` : ""}
									</span>
								</div>
								<button
									type="button"
									className={BTN_GHOST}
									onClick={() => deleteMut.mutate(row.id)}
									disabled={deleteMut.isPending}
									data-testid={`custom-fraud-rule-engine-per-campaign-per-partner-delete-${row.id}`}
								>
									Delete
								</button>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default CustomFraudRuleEnginePerCampaignPerPartnerPage;
